import { colors, weights } from '@components/theme/styles/index';
import { useAuth } from '@gen2/hooks';
import {
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  Logo,
  FileInviteButton,
  ErrorDisplayBox,
} from './SentryErrorModal.styled';

interface SentryErrorModalProps {
  open: boolean;
  onClose: () => void;
  error?: Error;
  eventId?: string;
}

export const SentryErrorModal: React.FC<SentryErrorModalProps> = ({
  open,
  onClose,
  error,
  eventId,
}) => {
  const { t } = useTranslation('sentryErrorModal');
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { user } = useAuth();

  // Reset state when modal opens/closes and prepopulate with user data
  useEffect(() => {
    if (!open) {
      setFeedback('');
      setEmail('');
      setName('');
      setIsSubmitted(false);
      setSubmitError(null);
      setIsSubmitting(false);
    } else if (user?.email) {
      // Prepopulate email when modal opens
      setEmail(user.email);
      // Prepopulate name when modal opens
      if (user?.first_name && user?.last_name) {
        setName(`${user.first_name} ${user.last_name}`);
      } else if (user?.email) {
        setName(user.email.split('@')[0]);
      }
    }
  }, [open, user?.email, user?.first_name, user?.last_name]);

  const handleSubmit = useCallback(async () => {
    if (!eventId) {
      setSubmitError(t('noEventIdError'));
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await Sentry.captureUserFeedback({
        event_id: eventId,
        email,
        comments: feedback,
        name:
          name ||
          ((user?.first_name && user?.last_name
            ? `${user.first_name} ${user.last_name}`
            : user?.email?.split('@')[0] || 'Anonymous') as string),
      });

      setIsSubmitted(true);
    } catch (err) {
      setSubmitError(t('submitError'));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    eventId,
    email,
    feedback,
    name,
    t,
    user?.first_name,
    user?.last_name,
    user?.email,
  ]);

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      // Reset state immediately when closing
      setFeedback('');
      setEmail('');
      setName('');
      setIsSubmitted(false);
      setSubmitError(null);
      setIsSubmitting(false);
      onClose();
    }
  }, [isSubmitting, onClose]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
    },
    [],
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value);
    },
    [],
  );

  const handleFeedbackChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFeedback(e.target.value);
    },
    [],
  );

  // Don't render anything if modal is not open
  if (!open) {
    return null;
  }

  if (isSubmitted) {
    return (
      <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          <Logo src="/assets/logo-icon--dark.svg" alt="FileInvite" />
          <Typography
            variant="body1"
            sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}
          >
            {t('successTitle')}
          </Typography>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Alert severity="success">{t('successMessage')}</Alert>
        </StyledDialogContent>
        <StyledDialogActions>
          <FileInviteButton onClick={handleClose} variant="contained">
            Close
          </FileInviteButton>
        </StyledDialogActions>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <StyledDialogTitle>
        <Logo src="/assets/logo-icon--dark.svg" alt="FileInvite" />
        <Typography
          variant="body1"
          sx={{ fontWeight: 'bold', fontSize: '1.25rem' }}
        >
          {t('title')}
        </Typography>
      </StyledDialogTitle>

      <StyledDialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('subtitle')}
        </Typography>

        {error && (
          <ErrorDisplayBox>
            <Typography
              variant="caption"
              color={colors.primaryDark2}
              fontWeight={weights.medium}
            >
              {t('errorPrefix')} {error.message}
            </Typography>
          </ErrorDisplayBox>
        )}

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <TextField
          fullWidth
          label={t('emailLabel')}
          type="email"
          value={email}
          onChange={handleEmailChange}
          margin="normal"
          placeholder={t('emailPlaceholder') as string}
        />

        <TextField
          fullWidth
          label={t('nameLabel')}
          value={name}
          onChange={handleNameChange}
          margin="normal"
          placeholder={t('namePlaceholder') as string}
        />

        <TextField
          fullWidth
          label={t('feedbackLabel')}
          multiline
          rows={4}
          value={feedback}
          onChange={handleFeedbackChange}
          margin="normal"
          placeholder={t('feedbackPlaceholder') as string}
        />
      </StyledDialogContent>

      <StyledDialogActions>
        <Button onClick={handleClose} disabled={isSubmitting} color="inherit">
          {t('cancelButton')}
        </Button>
        <FileInviteButton
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !feedback.trim()}
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {isSubmitting ? t('submittingText') : t('submitButton')}
        </FileInviteButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default SentryErrorModal;
